// frontend/src/api/timesheet.ts
import axiosInstance from './axiosConfig';
import { TimeSheet, WeeklySubmission } from '@/types/index';

// Add this function for dashboard data
export const getDashboardData = async () => {
  try {
    const { data } = await axiosInstance.get('/api/v1/dashboard');
    return data;
  } catch (error) {
    console.error('Get dashboard data error:', error);
    throw error;
  }
};

export const exportTimesheetData = async (format: 'excel' | 'csv' = 'excel') => {
  try {
    const response = await axiosInstance.get('/api/v1/export-data', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Export timesheet data error:', error);
    throw error;
  }
};

export const saveTimesheet = async (timesheet: TimeSheet) => {
  try {
    const { data } = await axiosInstance.post('/api/timesheet', timesheet);
    return data;
  } catch (error) {
    console.error('Save timesheet error:', error);
    throw error;
  }
};

export const getTimesheet = async (weekStart: string) => {
  try {
    const { data } = await axiosInstance.get(`/api/timesheet/${weekStart}`);
    return data;
  } catch (error) {
    console.error('Get timesheet error:', error);
    throw error;
  }
};

export const getWeeklySubmissions = async (weekStart: string): Promise<WeeklySubmission[]> => {
  try {
    const { data } = await axiosInstance.get(`/api/admin/submissions/${weekStart}`);
    return data;
  } catch (error) {
    console.error('Get submissions error:', error);
    throw error;
  }
};

export const downloadTimesheet = async (weekStart: string, format: 'csv' | 'excel' = 'excel') => {
  try {
    const response = await axiosInstance.get(
      `/api/timesheet/download/${weekStart}?format=${format}`,
      {
        responseType: 'blob'
      }
    );

    // Create a blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `timesheet-${weekStart}.${format === 'csv' ? 'csv' : 'xlsx'}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Download timesheet error:', error);
    throw error;
  }
};

export const updateTimesheetEntry = async (entryId: string, updatedData: {
  Monday?: number;
  Tuesday?: number;
  Wednesday?: number;
  Thursday?: number;
  Friday?: number;
  status?: string;
}) => {
  const response = await fetch(`/api/admin/timesheet/${entryId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updatedData),
  });

  if (!response.ok) {
    throw new Error('Failed to update timesheet entry');
  }

  return response.json();
};

export const updateWeeklyTimesheet = async (weekStartDate: string, timesheetData: {
  Monday?: number;
  Tuesday?: number;
  Wednesday?: number;
  Thursday?: number;
  Friday?: number;
  Saturday?: number;
  Sunday?: number;
  total_hours?: number;
  status?: string;
  user_email?: string; // Add user_email for admin updates
}) => {
  try {
    // Use the admin update endpoint
    const { data } = await axiosInstance.put('/api/v1/admin/update-by-week', {
      user_email: timesheetData.user_email,
      weekStartDate,
      monday: timesheetData.Monday || 0,
      tuesday: timesheetData.Tuesday || 0,
      wednesday: timesheetData.Wednesday || 0,
      thursday: timesheetData.Thursday || 0,
      friday: timesheetData.Friday || 0,
      saturday: timesheetData.Saturday || 0,
      sunday: timesheetData.Sunday || 0,
      status: timesheetData.status || 'draft',
      comments: '' // Add empty comments as required by API
    });
    return data;
  } catch (error) {
    console.error('Update timesheet error:', error);
    throw error;
  }
};